import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useLocationParams } from 'hooks';
import { getAccounts } from '../api/getAccounts';
import queryKeys from '../api/queryKeys';
import {
  isDeepLinkStepPass,
  resolveFetchedStepStatus,
} from '../utils/deepLinkInit';
import type { DeepLinkStepGate, DeepLinkStepResult } from './deepLinkTypes';

const NOOP_RESTORE = () => undefined;

const useDeepLinkAdAccountsStep = ({
  enabled,
}: DeepLinkStepGate): DeepLinkStepResult => {
  const formApp = useWatch({ name: 'app' }) as string | undefined;
  const {
    app: appParam,
    platform: platformParam,
    adAccounts: adAccountsParam,
  } = useLocationParams();
  const resolvedApp = appParam ?? formApp;

  const accountsQuery = useQuery({
    queryKey: [...queryKeys.adAccounts, [platformParam, resolvedApp].join(',')],
    queryFn: ({ signal }) =>
      getAccounts(
        { platform: platformParam ?? undefined, app: resolvedApp ?? undefined },
        signal
      ),
    enabled: enabled && adAccountsParam.length > 0,
    staleTime: 0,
  });

  const missing = useMemo(() => {
    const accounts = accountsQuery.data;
    if (!accounts) return [];
    return adAccountsParam.filter(
      (id) => !accounts.some((account) => account.ad_account_id === id)
    );
  }, [accountsQuery.data, adAccountsParam]);

  const status = resolveFetchedStepStatus({
    enabled,
    emptySuccess: adAccountsParam.length === 0,
    isPending: accountsQuery.isPending,
    isError: accountsQuery.isError,
    hasMissing: missing.length > 0,
  });

  return {
    status,
    pass: isDeepLinkStepPass(enabled, status),
    missing,
    restore: NOOP_RESTORE,
  };
};

export default useDeepLinkAdAccountsStep;
